import type { ReactNode } from 'react';
import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react';

export type TonoBanner = 'info' | 'aviso' | 'alerta' | 'exito';

interface Props {
  tono: TonoBanner;
  titulo: string;
  children?: ReactNode;
  /** Acción principal, por ejemplo el botón de respaldar. */
  accion?: ReactNode;
}

/**
 * Aviso dentro de la app, con el estilo de Foco: un fondo suave sin borde y
 * un ícono de trazo fino.
 *
 * Cada tono lleva un ícono distinto y una etiqueta para lectores de pantalla
 * además del color, para no depender solo del color (Req. 8.5).
 *
 * Origen: Control de Negocio de Comida.
 */
const TONOS: Record<TonoBanner, { fondo: string; color: string; icono: LucideIcon; etiqueta: string }> =
  {
    info: { fondo: 'bg-info-suave', color: 'text-info', icono: Info, etiqueta: 'Información' },
    aviso: { fondo: 'bg-aviso-suave', color: 'text-aviso', icono: TriangleAlert, etiqueta: 'Aviso' },
    alerta: {
      fondo: 'bg-alerta-suave',
      color: 'text-alerta',
      icono: CircleAlert,
      etiqueta: 'Alerta',
    },
    exito: { fondo: 'bg-exito-suave', color: 'text-exito', icono: CircleCheck, etiqueta: 'Listo' },
  };

export function Banner({ tono, titulo, children, accion }: Props) {
  const { fondo, color, icono: Icono, etiqueta } = TONOS[tono];

  return (
    <div className={`rounded-[20px] p-4 ${fondo}`} role={tono === 'alerta' ? 'alert' : 'status'}>
      <div className="flex gap-3">
        <Icono className={`mt-px size-5 ${color}`} />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium leading-snug text-texto">
            <span className="sr-only">{etiqueta}: </span>
            {titulo}
          </p>
          {children !== undefined && (
            <div className="mt-1 text-[13px] leading-relaxed text-texto-suave">{children}</div>
          )}
          {accion !== undefined && <div className="mt-3">{accion}</div>}
        </div>
      </div>
    </div>
  );
}
